/**
 * useFavorites.js — PC 사이드바 즐겨찾기 상태 (2026-10-22)
 *
 * 불러오기·넣기·빼기·누르기. 저장은 개인 설정 desktop_favorites(계정에 저장 — 다른 PC 에서도 같다).
 * 재료(보이는 조건·작업 대상)는 favoriteCatalog.js.
 *
 * - 휴대폰 화면에서는 쉰다(active=false) — 휴대폰은 위쪽 바로가기가 따로 있다(사용자 선택 "따로 씀").
 * - 다 불러오기 전(내 열쇠·저장값)에는 별을 감춘다 — 반쯤 불러온 상태로 저장하면 다른 칸이 지워질 수 있다.
 * - 5칸이 찼으면 더 넣지 않는다. 지금 열 수 없어 숨은 칸은 사용자가 뺄 수 없으므로, 자리가 모자랄 때만 그것부터 비운다.
 * - 누르면 저장해 둔 작업 대상으로 바꾸고 그 화면으로 간다 — 화면과 작업 대상 칸이 같은 말을 하게(2026-09-14 규칙).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../api/apiClient';
import {
  FAVORITE_KEY, FAVORITE_MAX, favKey, placeOf, toSaved, normalizeFavorites, buildFavoriteCatalog, resolveFavorites,
} from './favoriteCatalog';

const upper = (v) => String(v || '').trim().toUpperCase();

export function useFavorites({ active, cats, permissions, permCodes, isAdmin, scope, myMenus, fallbackIntegrated }) {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(null); // null = 아직 못 불러옴(실패도 그대로 둔다 — 빈 목록으로 덮어쓰지 않게)

  useEffect(() => {
    if (!active || saved !== null) return undefined;
    let alive = true;
    apiClient.get(`/api/my/preferences/${FAVORITE_KEY}`)
      .then((r) => { if (alive && r?.ok) setSaved(normalizeFavorites(r.data?.value)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [active, saved]);

  const ready = Boolean(active && saved !== null && permCodes instanceof Set
    && (!isAdmin || ((scope?.tenants?.length || 0) > 0 && scope?.companiesReady)));

  const catalog = useMemo(
    () => (ready ? buildFavoriteCatalog({ cats, permissions, permCodes, isAdmin, scope, myMenus, fallbackIntegrated }) : new Map()),
    [ready, cats, permissions, permCodes, isAdmin, scope, myMenus, fallbackIntegrated],
  );
  const items = useMemo(
    () => (ready ? resolveFavorites(saved, catalog, { isAdmin, scope }) : []),
    [ready, saved, catalog, isAdmin, scope],
  );
  const shownKeys = useMemo(() => new Set(items.map(favKey)), [items]);

  const persist = useCallback(async (next) => {
    const prev = saved;
    setSaved(next);
    const r = await apiClient.put(`/api/my/preferences/${FAVORITE_KEY}`, { value: next.map(toSaved) }).catch(() => null);
    if (!r?.ok) setSaved(prev); // 저장 실패 — 되돌린다
  }, [saved]);

  const isStarred = useCallback((to) => shownKeys.has(favKey({ to, ...placeOf(to, scope) })), [shownKeys, scope]);

  const toggle = useCallback(({ to, label }) => {
    if (!ready) return;
    const entry = { to, label, ...placeOf(to, scope) };
    const k = favKey(entry);
    if (saved.some((f) => favKey(f) === k)) { persist(saved.filter((f) => favKey(f) !== k)); return; }
    if (items.length >= FAVORITE_MAX) return;
    const base = saved.length >= FAVORITE_MAX ? saved.filter((f) => shownKeys.has(favKey(f))) : saved;
    persist([...base, entry]);
  }, [ready, scope, saved, items, shownKeys, persist]);

  const remove = useCallback((entry) => {
    if (!ready) return;
    persist(saved.filter((f) => favKey(f) !== favKey(entry)));
  }, [ready, saved, persist]);

  const open = useCallback((entry) => {
    if (scope?.isOperator && entry.tenant && upper(entry.tenant) !== upper(scope.selectedTenant?.code)) {
      scope.selectTenant(entry.tenant, { company: entry.company, to: entry.to });
      return;
    }
    if (scope && entry.company && upper(entry.company) !== upper(scope.company?.code)) {
      scope.selectCompany(entry.company, { to: entry.to });
      return;
    }
    navigate(entry.to);
  }, [scope, navigate]);

  // 지금 이 칸의 화면을 보고 있나 — 주소와 작업 대상이 둘 다 같아야
  const isHere = useCallback((entry, pathname) => pathname === entry.to
    && (!entry.company || upper(entry.company) === upper(scope?.company?.code))
    && (!entry.tenant || upper(entry.tenant) === upper(scope?.selectedTenant?.code)), [scope]);

  return {
    enabled: ready, items, max: FAVORITE_MAX, canAdd: items.length < FAVORITE_MAX, isStarred, toggle, remove, open, isHere,
  };
}
